import { supabase } from "@/integrations/supabase/client";
import { MentorWithDetails } from "@/types/mentor";
import { getMentorshipOfferings } from "./mentorOfferings";
import { useToast } from "@/components/ui/use-toast";

export const fetchMentorDetails = async (mentorIdOrSlug: string): Promise<MentorWithDetails> => {
  try {
    console.log('🔄 [fetchMentorDetails] Buscando detalhes do mentor:', mentorIdOrSlug);
    let mentorInfoData;
    let profileData;

    // Verificar se é um UUID (ID) ou uma slug
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(mentorIdOrSlug);
    console.log('🔍 [fetchMentorDetails] Tipo de ID:', isUUID ? 'UUID' : 'Slug');

    if (isUUID) {
      // Buscar por ID do mentor_info
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select(`
          id,
          bio,
          image_url,
          cover_url,
          is_active,
          profile_id,
          youtube_url,
          instagram_url,
          discord_url,
          twitch_url,
          video_url,
          achievements
        `)
        .eq('id', mentorIdOrSlug)
        .single();

      if (mentorError || !mentorData) {
        console.error('❌ [fetchMentorDetails] Erro ao buscar mentor:', mentorError);
        throw new Error(`Mentor não encontrado: ${mentorError?.message || 'Erro desconhecido'}`);
      }

      console.log('✅ [fetchMentorDetails] Dados do mentor encontrados:', mentorData);
      console.log('🔗 [fetchMentorDetails] Links sociais brutos do banco:', {
        youtube: mentorData.youtube_url,
        instagram: mentorData.instagram_url,
        discord: mentorData.discord_url,
        twitch: mentorData.twitch_url
      });

      mentorInfoData = mentorData;

      // Buscar profile pelo profile_id
      const { data: profileResult, error: profileError } = await supabase
        .from('profiles')
        .select('id, nickname, email, slug')
        .eq('id', mentorInfoData.profile_id)
        .single();

      if (profileError || !profileResult) {
        console.error('❌ [fetchMentorDetails] Erro ao buscar perfil:', profileError);
        throw new Error(`Perfil do mentor não encontrado: ${profileError?.message || 'Erro desconhecido'}`);
      }

      console.log('✅ [fetchMentorDetails] Dados do perfil encontrados:', profileResult);
      profileData = profileResult;
    } else {
      // Buscar por slug do profile
      const { data: profileResult, error: profileError } = await supabase
        .from('profiles')
        .select('id, nickname, email, slug')
        .eq('slug', mentorIdOrSlug)
        .single();

      if (profileError || !profileResult) {
        throw new Error(`Perfil do mentor não encontrado: ${profileError?.message || 'Erro desconhecido'}`);
      }

      profileData = profileResult;

      // Buscar mentor_info pelo profile_id
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select(`
          id,
          bio,
          image_url,
          cover_url,
          is_active,
          profile_id,
          youtube_url,
          instagram_url,
          discord_url,
          twitch_url,
          video_url,
          achievements
        `)
        .eq('profile_id', profileData.id)
        .single();

      if (mentorError || !mentorData) {
        throw new Error(`Mentor não encontrado: ${mentorError?.message || 'Erro desconhecido'}`);
      }

      console.log('✅ [fetchMentorDetails] Dados do mentor encontrados (por slug):', mentorData);
      console.log('🔗 [fetchMentorDetails] Links sociais brutos do banco (por slug):', {
        youtube: mentorData.youtube_url,
        instagram: mentorData.instagram_url,
        discord: mentorData.discord_url,
        twitch: mentorData.twitch_url
      });

      mentorInfoData = mentorData;
    }

    // Verificar se o mentor está ativo
    if (!mentorInfoData.is_active) {
      throw new Error('Este mentor não está ativo no momento');
    }

    // Buscar ofertas do mentor
    const { data: offeringsData, error: offeringsError } = await supabase
      .from('mentorship_offerings')
      .select(`
        id,
        title,
        description,
        price_cents,
        duration_minutes,
        is_active,
        is_group,
        max_participants,
        display_order,
        cover_image_url,
        game:game_id (
          id,
          name,
          cover_url
        )
      `)
      .eq('mentor_id', mentorInfoData.id)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (offeringsError) {
      console.error(`Erro ao buscar ofertas do mentor ${mentorInfoData.id}:`, offeringsError);
      throw new Error(`Erro ao buscar ofertas do mentor: ${offeringsError.message}`);
    }

    // Buscar avaliações do mentor de session_reviews (usando profile_id)
    const { data: ratingsData, error: ratingsError } = await supabase
      .from('session_reviews')
      .select('rating')
      .eq('mentor_id', mentorInfoData.profile_id);

    if (ratingsError) {
      console.error(`Erro ao buscar avaliações do mentor ${mentorInfoData.profile_id}:`, ratingsError);
      // Não falhar se não houver avaliações - apenas logar o erro
      console.warn('⚠️ [fetchMentorDetails] Continuando sem avaliações...');
    }

    // Calcular média das avaliações (considera todas, não apenas públicas)
    const averageRating = ratingsData && ratingsData.length > 0
      ? ratingsData.reduce((acc, curr) => acc + curr.rating, 0) / ratingsData.length
      : 0;
    
    console.log('⭐ [fetchMentorDetails] Média de avaliações calculada:', {
      averageRating,
      totalReviews: ratingsData?.length || 0
    });

    // Extrair jogos únicos das ofertas
    const uniqueGames = offeringsData
      ? Array.from(new Set(offeringsData.map(o => o.game).filter(Boolean)))
      : [];

    // Encontrar o menor preço entre as ofertas ativas
    const minimumPrice = offeringsData && offeringsData.length > 0
      ? Math.min(...offeringsData.map(o => o.price_cents))
      : null;

    // Extrair nome do mentor
    const mentorName = profileData.nickname || `Mentor ${mentorInfoData.id.substring(0, 4)}`;

    // Converter achievements para o formato correto com tratamento de erro
    let typedAchievements = [];
    if (mentorInfoData.achievements) {
      try {
        // Verifica se já é um objeto (array)
        if (Array.isArray(mentorInfoData.achievements)) {
          typedAchievements = mentorInfoData.achievements;
        } else {
          // Tenta fazer o parse se for uma string
          typedAchievements = JSON.parse(mentorInfoData.achievements);
        }
      } catch (error) {
        console.error('Erro ao fazer parse dos achievements:', error);
        typedAchievements = [];
      }
    }

    // 🎯 Buscar comunidades ativas do mentor
    console.log('🏘️ [fetchMentorDetails] Buscando comunidades do mentor:', mentorInfoData.profile_id);
    let mentorCommunities = [];
    try {
      const { data: communitiesData, error: communitiesError } = await supabase
        .from('communities')
        .select(`
          id,
          name,
          slug,
          description,
          cover_image_url,
          member_count,
          game_id,
          games!communities_game_id_fkey (
            id,
            name
          )
        `)
        .eq('mentor_id', mentorInfoData.profile_id)
        .eq('is_active', true)
        .order('member_count', { ascending: false })
        .limit(6); // Limitar a 6 comunidades para não sobrecarregar

      if (communitiesError) {
        console.warn('⚠️ [fetchMentorDetails] Erro ao buscar comunidades (tentando com owner_id):', communitiesError);
        
        // Tentar com owner_id como fallback
        const { data: communitiesOwnerData, error: communitiesOwnerError } = await supabase
          .from('communities')
          .select(`
            id,
            name,
            slug,
            description,
            cover_image_url,
            member_count,
            game_id,
            games!communities_game_id_fkey (
              id,
              name
            )
          `)
          .eq('owner_id', mentorInfoData.profile_id)
          .eq('is_active', true)
          .order('member_count', { ascending: false })
          .limit(6);

        if (!communitiesOwnerError && communitiesOwnerData) {
          mentorCommunities = communitiesOwnerData.map(community => ({
            id: community.id,
            name: community.name,
            slug: community.slug,
            description: community.description,
            cover_image_url: community.cover_image_url,
            member_count: community.member_count || 0,
            game: community.games ? {
              id: community.games.id,
              name: community.games.name
            } : null
          }));
          console.log('✅ [fetchMentorDetails] Comunidades encontradas (owner_id):', mentorCommunities.length);
        }
      } else if (communitiesData) {
        mentorCommunities = communitiesData.map(community => ({
          id: community.id,
          name: community.name,
          slug: community.slug,
          description: community.description,
          cover_image_url: community.cover_image_url,
          member_count: community.member_count || 0,
          game: community.games ? {
            id: community.games.id,
            name: community.games.name
          } : null
        }));
        console.log('✅ [fetchMentorDetails] Comunidades encontradas (mentor_id):', mentorCommunities.length);
      }
    } catch (error) {
      console.warn('⚠️ [fetchMentorDetails] Erro ao buscar comunidades do mentor:', error);
      // Continua sem comunidades ao invés de falhar
    }

    // Retornar dados completos do mentor
    const formattedData = {
      id: mentorInfoData.id,
      profile_id: mentorInfoData.profile_id,
      slug: profileData.slug,
      bio: mentorInfoData.bio,
      image_url: mentorInfoData.image_url,
      cover_url: mentorInfoData.cover_url,
      is_active: mentorInfoData.is_active ?? true,
      name: mentorName,
      average_rating: averageRating,
      minimum_price: minimumPrice,
      video_url: mentorInfoData.video_url, // 🎯 Campo para vídeo da página pública
      games: uniqueGames,
      social: {
        youtube: mentorInfoData.youtube_url,
        instagram: mentorInfoData.instagram_url,
        discord: mentorInfoData.discord_url,
        twitch: mentorInfoData.twitch_url,
      },
      achievements: typedAchievements,
      offerings: offeringsData || [],
      communities: mentorCommunities // 🎯 Comunidades ativas do mentor
    } as MentorWithDetails;

    console.log('✅ [fetchMentorDetails] Dados formatados:', formattedData);
    console.log('🔗 [fetchMentorDetails] Links sociais formatados:', formattedData.social);

    return formattedData;
  } catch (error) {
    console.error(`❌ [fetchMentorDetails] Erro ao buscar mentor ${mentorIdOrSlug}:`, error);
    throw error;
  }
};
